import type { Certificate } from '@/lib/db';

const EXCLUDED_LIFECYCLES = ['no_expiry', 'no_five_year_revalidation', 'unknown_verify'];

export function isAlertSuppressed(cert: Certificate): boolean {
  return cert.alertSuppressed === true;
}

export function isAlertableCertificate(cert: Certificate): boolean {
  return (
    !isAlertSuppressed(cert) &&
    cert.status !== 'no-expiry' &&
    cert.status !== 'verify' &&
    !EXCLUDED_LIFECYCLES.includes(cert.lifecycleType ?? '')
  );
}

export function needsExpiryAttention(cert: Certificate): boolean {
  return (
    isAlertableCertificate(cert) &&
    (cert.status === 'expired' || cert.status === 'critical' || cert.status === 'expiring-soon')
  );
}

export type AttentionSeverity = 'critical' | 'warning' | 'information';

export type CertificateAttentionItem = {
  certificate: Certificate;
  severity: AttentionSeverity;
  reason: string;
};

const ATTENTION_SEVERITY_ORDER: Record<AttentionSeverity, number> = {
  critical: 0,
  warning: 1,
  information: 2,
};

function appendAction(context: string, actionLabel?: string): string {
  return actionLabel ? `${context} · ${actionLabel}` : context;
}

function attentionTimeContext(certificate: Certificate): string {
  const days = certificate.daysRemaining;

  if (certificate.status === 'expired') {
    if (days === null) return appendAction('Expired', certificate.actionLabel);
    const elapsed = Math.abs(days);
    return appendAction(`Expired ${elapsed} day${elapsed !== 1 ? 's' : ''} ago`, certificate.actionLabel);
  }

  if (days === 0) return appendAction('Expires today', certificate.actionLabel);
  if (days !== null) {
    return appendAction(`Expires in ${days} day${days !== 1 ? 's' : ''}`, certificate.actionLabel);
  }

  return certificate.actionLabel ?? 'Check expiry';
}

/**
 * Returns actionable certificate issues sorted by severity, then urgency.
 * This is an operational triage list, not a claim about joining eligibility.
 */
export function getCertificateAttentionItems(certificates: Certificate[]): CertificateAttentionItem[] {
  return certificates
    .flatMap<CertificateAttentionItem>((certificate) => {
      if (isAlertSuppressed(certificate)) return [];

      if (isRequirementReview(certificate)) {
        return [{ certificate, severity: 'information', reason: 'Verify requirement' }];
      }

      if (certificate.status === 'expired' && isAlertableCertificate(certificate)) {
        return [{ certificate, severity: 'critical', reason: attentionTimeContext(certificate) }];
      }

      if (certificate.status === 'critical' && isAlertableCertificate(certificate)) {
        return [{ certificate, severity: 'critical', reason: attentionTimeContext(certificate) }];
      }

      if (certificate.status === 'expiring-soon' && isAlertableCertificate(certificate)) {
        return [{ certificate, severity: 'warning', reason: attentionTimeContext(certificate) }];
      }

      if (isAlertableCertificate(certificate) && certificate.daysRemaining === null) {
        return [{ certificate, severity: 'information', reason: 'Expiry date unknown' }];
      }

      return [];
    })
    .sort((a, b) => {
      const severity = ATTENTION_SEVERITY_ORDER[a.severity] - ATTENTION_SEVERITY_ORDER[b.severity];
      if (severity !== 0) return severity;

      const aDays = a.certificate.daysRemaining ?? Number.MAX_SAFE_INTEGER;
      const bDays = b.certificate.daysRemaining ?? Number.MAX_SAFE_INTEGER;
      if (aDays !== bDays) return aDays - bDays;

      if (a.severity === 'information' && b.severity === 'information') {
        const informationOrder = (item: CertificateAttentionItem): number =>
          item.reason === 'Expiry date unknown' ? 0 : 1;
        const informationPriority = informationOrder(a) - informationOrder(b);
        if (informationPriority !== 0) return informationPriority;
      }

      return a.certificate.name.localeCompare(b.certificate.name);
    });
}

export type CertificateHealth = {
  valid: number;
  expiringSoon: number;
  expired: number;
  review: number;
  coveredHistorical: number;
};

export function computeCertificateHealth(certificates: Certificate[]): CertificateHealth {
  return certificates.reduce<CertificateHealth>(
    (health, cert) => {
      if (isAlertSuppressed(cert)) {
        health.coveredHistorical += 1;
      } else if (isRequirementReview(cert)) {
        health.review += 1;
      } else if (isAlertableCertificate(cert) && cert.status === 'expired') {
        health.expired += 1;
      } else if (
        isAlertableCertificate(cert) &&
        (cert.status === 'critical' || cert.status === 'expiring-soon')
      ) {
        health.expiringSoon += 1;
      } else {
        health.valid += 1;
      }

      return health;
    },
    { valid: 0, expiringSoon: 0, expired: 0, review: 0, coveredHistorical: 0 }
  );
}

export type CertificateInsights = {
  oldestExpired: Certificate | null;
  nextUpcomingExpiry: Certificate | null;
};

/**
 * Returns factual expiry extremes for dashboard interpretation.
 * Suppressed historical and requirement-review records are excluded.
 */
export function computeCertificateInsights(certificates: Certificate[]): CertificateInsights {
  const withKnownExpiry = certificates
    .filter(isAlertableCertificate)
    .filter((cert) => cert.daysRemaining !== null);

  const oldestExpired =
    withKnownExpiry
      .filter((cert) => cert.daysRemaining! < 0)
      .sort((a, b) => a.daysRemaining! - b.daysRemaining!)[0] ?? null;

  const nextUpcomingExpiry =
    withKnownExpiry
      .filter((cert) => cert.daysRemaining! >= 0)
      .sort((a, b) => a.daysRemaining! - b.daysRemaining!)[0] ?? null;

  return { oldestExpired, nextUpcomingExpiry };
}

// ── Certificate Readiness v1 ──────────────────────────────────────────────────

/**
 * A cert that is not suppressed, not alertable, and not a true permanent record.
 * This covers verify-status certs AND excluded-lifecycle certs such as
 * no_five_year_revalidation with an explicit expiry — both need user verification
 * regardless of what raw status the builder assigned them.
 */
export function isRequirementReview(cert: Certificate): boolean {
  return (
    !isAlertSuppressed(cert) &&
    !isAlertableCertificate(cert) &&
    cert.status !== 'no-expiry'
  );
}

export type ReadinessStatus = 'expired' | 'renewal' | 'review' | 'ok';

export type ReadinessV1 = {
  status: ReadinessStatus;
  /** Count of non-suppressed alertable certs that are expired, critical, or expiring-soon. */
  attentionCount: number;
  /** Count of non-suppressed, non-alertable, non-permanent certs needing requirement review. */
  reviewCount: number;
  /** Most urgent alertable cert with a known expiry (most negative daysRemaining first). */
  nextExpiry: { name: string; daysRemaining: number } | null;
};

/**
 * Computes the v1 Certificate Readiness summary from a certificate list.
 * Returns null when the list is empty (no data imported yet).
 *
 * Status precedence:
 *   expired → renewal → review → ok
 *
 * Suppressed historical certs are excluded from all counts and status signals.
 * Review certs (uncertain lifecycle, including no_five_year_revalidation with
 * explicit dates) are identified by isRequirementReview and counted separately.
 */
export function computeReadinessV1(certificates: Certificate[]): ReadinessV1 | null {
  if (certificates.length === 0) return null;

  const alertable = certificates.filter(isAlertableCertificate);

  const hasExpired = alertable.some((c) => c.status === 'expired');
  const hasRenewal = alertable.some((c) => c.status === 'critical' || c.status === 'expiring-soon');
  const reviewCerts = certificates.filter(isRequirementReview);
  const hasReview = reviewCerts.length > 0;

  const attentionCount = alertable.filter(needsExpiryAttention).length;
  const reviewCount = reviewCerts.length;

  // Most urgent: sort ascending by daysRemaining so expired (negative) come first.
  const withExpiry = alertable
    .filter((c) => c.daysRemaining !== null)
    .sort((a, b) => (a.daysRemaining ?? 0) - (b.daysRemaining ?? 0));

  const nextExpiry =
    withExpiry.length > 0
      ? { name: withExpiry[0].name, daysRemaining: withExpiry[0].daysRemaining! }
      : null;

  const status: ReadinessStatus =
    hasExpired ? 'expired' : hasRenewal ? 'renewal' : hasReview ? 'review' : 'ok';

  return { status, attentionCount, reviewCount, nextExpiry };
}

// ── Expiry Timeline ───────────────────────────────────────────────────────────

export type ExpiryTimelineGroups = {
  expired: Certificate[];
  within30: Certificate[];
  within90: Certificate[];
  longTermValid: Certificate[];
  verifyRequirement: Certificate[];
  dateUnknown: Certificate[];
};

/**
 * Groups non-suppressed certificates into deterministic expiry timeline buckets.
 * Suppressed/historical certs are excluded — they remain in the cert table only.
 * Each non-suppressed cert belongs to exactly one group.
 *
 * Group precedence for non-suppressed certs:
 *   verifyRequirement → expired → within30 → within90 → dateUnknown → longTermValid
 *
 * Boundaries (daysRemaining):
 *   status 'expired' or days < 0   → expired
 *   0 – 30                          → within30
 *   31 – 90                         → within90
 *   > 90                            → longTermValid
 *   null + alertable + not expired  → dateUnknown (missing/unreadable expiry date)
 *   null + true no-expiry permanent → longTermValid
 */
export function groupExpiryTimeline(certificates: Certificate[]): ExpiryTimelineGroups {
  const groups: ExpiryTimelineGroups = {
    expired: [],
    within30: [],
    within90: [],
    longTermValid: [],
    verifyRequirement: [],
    dateUnknown: [],
  };

  for (const cert of certificates) {
    if (isAlertSuppressed(cert)) continue;

    if (isRequirementReview(cert)) {
      groups.verifyRequirement.push(cert);
    } else if (isAlertableCertificate(cert)) {
      const days = cert.daysRemaining;
      if (cert.status === 'expired' || (days !== null && days < 0)) {
        groups.expired.push(cert);
      } else if (days !== null && days <= 30) {
        groups.within30.push(cert);
      } else if (days !== null && days <= 90) {
        groups.within90.push(cert);
      } else if (days !== null) {
        groups.longTermValid.push(cert);
      } else {
        // alertable cert with null daysRemaining and non-expired status: date is missing
        groups.dateUnknown.push(cert);
      }
    } else {
      // non-alertable non-review: permanent no-expiry records
      groups.longTermValid.push(cert);
    }
  }

  const byDays = (a: Certificate, b: Certificate): number => {
    if (a.daysRemaining === null && b.daysRemaining === null) return 0;
    if (a.daysRemaining === null) return 1;
    if (b.daysRemaining === null) return -1;
    return a.daysRemaining - b.daysRemaining;
  };

  groups.expired.sort(byDays);
  groups.within30.sort(byDays);
  groups.within90.sort(byDays);
  groups.longTermValid.sort(byDays);
  groups.verifyRequirement.sort((a, b) => a.name.localeCompare(b.name));
  groups.dateUnknown.sort((a, b) => a.name.localeCompare(b.name));

  return groups;
}
